import cx from 'classnames';
import type {
  FunctionComponent,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
} from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useContext as useContext_selector } from 'use-context-selector';

import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import RadioUncheckedIcon from '@atlaskit/icon/core/radio-unchecked';
import SuccessIcon from '@atlaskit/icon/core/success';
import type { IconColor, TextColor } from '@atlaskit/tokens/css-type-schema';
import { TrelloCardAri } from '@atlassian/ari';
import type { SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import type { CardRole } from '@trello/card-roles';
import { sendErrorEvent } from '@trello/error-reporting';
// eslint-disable-next-line no-restricted-imports
import type {
  TrelloCard,
  TrelloCardBadgeDueInfo,
} from '@trello/graphql/generated';
import { intl } from '@trello/i18n';
import { useBoardId, useCardId, useListId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Tooltip } from '@trello/nachos/tooltip';
import type { CardTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { CardBackContext } from 'app/src/components/CardBack/CardBackContext';
import type { CardFrontSource } from 'app/src/components/CardFront/CardFront';
/**
 * This is the best way to add styles when the card front is hovered,
 * we have also added a condition so this styles only take place when the animateFromExternal is passed.
 */
// eslint-disable-next-line @trello/assets-alongside-implementation, @trello/less-matches-component
import cardFrontStyles from 'app/src/components/CardFront/CardFront.less';
import { CardFrontContext } from 'app/src/components/CardFront/CardFrontContext';
import { useArchiveCard } from 'app/src/components/CardFront/useArchiveCard';
import type { CardType } from 'app/src/components/CardType';
import { useCardType } from 'app/src/components/CardType';
import {
  useIsInboxBoard,
  useMemberInboxIds,
} from 'app/src/components/Inbox/useMemberInboxIds';
import { MirrorCardIdContext } from 'app/src/components/MirrorCard/MirrorCardIdContext';
import { useCardDoneStateBoardInfoFragment } from './CardDoneStateBoardInfo.generated';
import { useCardDoneStateCardInfoFragment } from './CardDoneStateCardInfo.generated';
import { CardDoneStateReadOnly } from './CardDoneStateReadOnly';
import { useUndoAutoArchivedCardMutation } from './UndoAutoArchivedCardMutation.generated';
import { useUpdateDueCompleteMutation } from './UpdateDueCompleteMutation.generated';
import { useCanEditCardDoneState } from './useCanEditCardDoneState';
import { useIsCardMarkedComplete } from './useIsCardMarkedComplete';
import { useIsDoneStateAnimationEnabled } from './useIsDoneStateAnimationEnabled';
import { useIsInboxAutoArchiveEnabled } from './useIsInboxAutoArchiveEnabled';

import styles from './CardDoneState.less';

/**
 * The source of the card done state
 */
export type DoneStateSource =
  | CardFrontSource
  | 'Inbox'
  | 'Workspace'
  | 'YourCards';

export interface CardDoneStateProps {
  animateFromExternal?: boolean;
  buttonClassName?: string;
  cardSource?: DoneStateSource;
  children?: ReactNode;
  childrenClassName?: string;
  containerClassName?: string;
  manualCompleteStyle?: boolean;
  readOnlyCustomIcon?: JSX.Element;
  showDoneStatebutton?: boolean;
  analyticsSource?: SourceType;
  successIconColor?: Exclude<TextColor, 'transparent'> | IconColor;
  uncheckedIconColor?:
    | Exclude<TextColor, 'transparent'>
    | IconColor
    | 'currentColor';
  isSmall?: boolean;
  isAriaHidden?: boolean;
}

const typeToRole = (cardType: CardType): CardRole => {
  if (
    cardType === 'default' ||
    cardType === 'cover' ||
    cardType === 'loading'
  ) {
    return null;
  }
  return cardType;
};

const colorfulLineColors = [
  token('color.icon.accent.magenta', '#cd519d'),
  token('color.icon.success', '#22A06B'),
  token('color.icon.accent.purple', '#8270db'),
  token('color.icon.accent.orange', '#e56910'),
  token('color.icon.accent.blue', '#1d7afc'),
  token('color.icon.success', '#22A06B'),
  token('color.icon.accent.magenta', '#cd519d'),
  token('color.icon.accent.teal', '#2898bd'),
];

export const CardDoneState: FunctionComponent<CardDoneStateProps> = ({
  animateFromExternal,
  buttonClassName,
  cardSource,
  children,
  childrenClassName,
  containerClassName,
  manualCompleteStyle,
  readOnlyCustomIcon,
  showDoneStatebutton = true,
  analyticsSource = 'cardView',
  successIconColor,
  uncheckedIconColor,
  isSmall,
  isAriaHidden = false,
}) => {
  const boardId = useBoardId();
  const memberId = useMemberId();
  const cardId = useCardId();
  const listId = useListId();
  const isInbox = useIsInboxBoard();
  const mirrorCardId = useContext(MirrorCardIdContext);

  const isInboxAutoArchiveEnabled = useIsInboxAutoArchiveEnabled();

  const isDoneStateAnimationEnabled = useIsDoneStateAnimationEnabled();
  const [isButtonAnimating, setIsButtonAnimating] = useState(false);
  const [isLineColorful, setIsLineColorful] = useState(false);

  const {
    cardType: cardTypeFront,
    cardFrontRef: { current: cardFront },
  } = useContext(CardFrontContext);
  const {
    cardType: cardTypeBack,
    dialogRef: { current: cardBack },
  } = useContext_selector(CardBackContext);
  const cardTypeOther = useCardType(cardId);

  const { data: card } = useCardDoneStateCardInfoFragment({
    from: { id: cardId },
    optimistic: true,
  });

  /**
   * This logic is a little brittle intrinsically, but very reliable in
   * practice.
   */
  const hasCardFrontContext = !!cardFront;
  const hasCardBackContext = !!cardBack;

  /**
   * Using the cardType from the contexts allows us to see if the card
   * is being mirrored.
   */
  const cardType = hasCardFrontContext
    ? cardTypeFront
    : hasCardBackContext
      ? cardTypeBack
      : cardTypeOther;

  /**
   * We can't use the hooks for fetching workspaceId and enterpriseId
   * as the providers we need are frequently not in the tree.
   */
  const { data: board } = useCardDoneStateBoardInfoFragment({
    from: { id: boardId },
    optimistic: true,
  });

  const workspaceId = board?.idOrganization;
  const enterpriseId = board?.idEnterprise;

  const hasCardCover = cardType === 'cover';
  const hasDueDate = !!card?.due;
  const hasLabels = !!card?.labels.length;
  const hasCardMembers = !!card?.idMembers.length;
  const hasCustomFields = !!card?.customFieldItems?.length; // TODO: seems like a type error here, customFieldItems is null in MultiBoardViews

  const isComplete = useIsCardMarkedComplete(cardId);
  const canEditCardDoneState = useCanEditCardDoneState(
    boardId,
    memberId,
    cardSource,
  );
  const { idBoard: idInboxBoard } = useMemberInboxIds();
  const cardOpenedFromInbox = idInboxBoard && boardId === idInboxBoard;

  const [updateDueComplete] = useUpdateDueCompleteMutation();

  const cardMarkedDoneFrom =
    cardSource === 'Planner'
      ? 'planner'
      : cardOpenedFromInbox
        ? 'inbox'
        : undefined;

  const archiveCard = useArchiveCard({
    source: analyticsSource,
    shouldShowFlag: false,
    // if we have a mirror card ID, we want to archive that instead of the source card
    targetCardId: mirrorCardId ?? cardId,
  });

  const [undoAutoArchivedCard] = useUndoAutoArchivedCardMutation();

  const handleUndoArchiveCard = useCallback(async () => {
    const taskName = isInbox ? 'edit-inbox-card/unarchive' : 'edit-card/closed';
    const traceId = Analytics.startTask({
      taskName,
      source: 'autoArchiveFlag',
    });
    try {
      await undoAutoArchivedCard({
        variables: {
          idCard: mirrorCardId ?? cardId,
          traceId,
        },
      });
      Analytics.taskSucceeded({
        taskName,
        source: 'autoArchiveFlag',
        traceId,
      });
      Analytics.sendTrackEvent({
        action: 'unarchived',
        actionSubject: 'card',
        source: analyticsSource,
        attributes: {
          isUndoAutoArchive: true,
          containerType: isInbox ? 'inbox' : undefined,
        },
      });
    } catch (error) {
      Analytics.taskFailed({
        taskName,
        source: 'autoArchiveFlag',
        traceId,
        error,
      });

      sendErrorEvent(error, {
        tags: {
          ownershipArea: 'trello-web-eng',
          feature: 'Inbox Auto Archive',
        },
      });
    }
  }, [undoAutoArchivedCard, cardId, isInbox, analyticsSource, mirrorCardId]);

  const handleArchiveCard = useCallback(async () => {
    const { success } = await archiveCard();
    if (success) {
      const dismissFlag = showFlag({
        id: 'card-done-state-auto-archive',
        // TODO: add translation
        title: `${card?.name} auto-archived`,
        icon: <ArchiveBoxIcon label="Archive" />,
        isAutoDismiss: true,
        msTimeout: 8000,
        actions: [
          {
            content: 'Undo',
            type: 'link',
            onClick: async () => {
              await handleUndoArchiveCard();
              dismissFlag();
            },
          },
        ],
        seed: card?.id,
      });
      Analytics.sendTrackEvent({
        action: 'archived',
        actionSubject: 'card',
        source: analyticsSource,
        attributes: {
          isAutoArchive: true,
          containerType: isInbox ? 'inbox' : undefined,
        },
      });
    } else {
      sendErrorEvent(new Error('Failed to auto archive inbox card'), {
        tags: {
          ownershipArea: 'trello-web-eng',
          feature: 'Inbox Auto Archive',
        },
      });
    }
  }, [
    archiveCard,
    card?.name,
    card?.id,
    handleUndoArchiveCard,
    isInbox,
    analyticsSource,
  ]);

  const handleDoneStatusUpdate = useCallback(async () => {
    const traceId = Analytics.startTask({
      taskName: 'edit-card/dueComplete',
      source: analyticsSource,
    });

    try {
      if (isDoneStateAnimationEnabled) {
        if (isComplete) {
          setIsLineColorful(false);
          setIsButtonAnimating(false);
        } else {
          const randomValue = Math.random();

          setIsLineColorful(randomValue < 0.05);
          setIsButtonAnimating(true);
        }
      }

      const cardAri = TrelloCardAri.create({
        workspaceId: workspaceId ?? '',
        cardId,
      }).toString();

      await updateDueComplete({
        variables: {
          traceId,
          cardId,
          dueComplete: !isComplete,
        },
        update(cache) {
          cache.modify<TrelloCard>({
            id: cache.identify({
              id: cardAri,
              __typename: 'TrelloCard',
            }),
            fields: {
              badges(existingBadges, { readField }) {
                if (!existingBadges) {
                  return existingBadges;
                }

                const due = readField<TrelloCardBadgeDueInfo>(
                  'due',
                  existingBadges,
                );
                return {
                  ...existingBadges,
                  due: due && { ...due, complete: !isComplete },
                };
              },
            },
          });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCardDueComplete: {
            id: cardId,
            dueComplete: !isComplete,
            badges: {
              __typename: 'Card_Badges',
              dueComplete: !isComplete,
            },
            __typename: 'Card',
          },
        },
      });

      Analytics.sendUpdatedCardFieldEvent({
        field: 'isComplete',
        source: analyticsSource,
        containers: formatContainers({
          listId,
          cardId,
          boardId,
          workspaceId,
          enterpriseId,
        }),
        attributes: {
          taskId: traceId,
          value: !isComplete,
          containerType: cardMarkedDoneFrom,
          cardRole: typeToRole(cardType),
          hasCardCover,
          hasDueDate,
          hasLabels,
          hasCardMembers,
          hasCustomFields,
        },
      });

      Analytics.taskSucceeded({
        taskName: 'edit-card/dueComplete',
        traceId,
        source: analyticsSource,
      });
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'edit-card/dueComplete',
        traceId,
        source: analyticsSource,
        error,
      });
      showFlag({
        id: 'card-front-due-complete',
        title: (
          <FormattedMessage
            id="templates.app_management.something-went-wrong"
            defaultMessage="Something went wrong. Please try again later."
            description="Error message when updating the due complete status of a card fails"
          />
        ),
        appearance: 'error',
        isAutoDismiss: true,
      });
    }
    // if card is not complete and we get to this point, its because the status is changing to complete and we can archive the card
    if (!isComplete && isInboxAutoArchiveEnabled && isInbox) {
      handleArchiveCard();
    }
  }, [
    analyticsSource,
    isDoneStateAnimationEnabled,
    updateDueComplete,
    cardId,
    isComplete,
    listId,
    boardId,
    workspaceId,
    enterpriseId,
    cardType,
    hasCardCover,
    hasDueDate,
    hasLabels,
    hasCardMembers,
    hasCustomFields,
    cardMarkedDoneFrom,
    handleArchiveCard,
    isInboxAutoArchiveEnabled,
    isInbox,
  ]);

  const updateCardDoneStatus: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await handleDoneStatusUpdate();
      },
      [handleDoneStatusUpdate],
    );

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLButtonElement>>(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        handleDoneStatusUpdate();
      }
    },
    [handleDoneStatusUpdate],
  );

  const getLineColor = useCallback(
    (index: number) => {
      if (successIconColor) {
        return successIconColor;
      }

      if (isLineColorful) {
        return colorfulLineColors[index];
      }
      return token('color.icon.success', '#22A06B');
    },
    [isLineColorful, successIconColor],
  );

  const resetAnimation = useCallback(() => {
    setIsLineColorful(false);
    setIsButtonAnimating(false);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleReducedMotionChange = () => {
      setIsButtonAnimating(false);
    };

    mediaQuery.addEventListener('change', handleReducedMotionChange);
    return () =>
      mediaQuery.removeEventListener('change', handleReducedMotionChange);
  }, []);

  if (!canEditCardDoneState) {
    return (
      <CardDoneStateReadOnly
        isComplete={isComplete}
        buttonClassName={buttonClassName}
        containerClassName={containerClassName}
        readOnlyCustomIcon={readOnlyCustomIcon}
      >
        {children}
      </CardDoneStateReadOnly>
    );
  }

  return (
    <div
      data-testid={getTestId<CardTestIds>('card-done-state')}
      className={cx(containerClassName, {
        [styles.cardDoneState]: true,
        [cardFrontStyles.cardFrontAnimateDoneState]: animateFromExternal,
      })}
    >
      {showDoneStatebutton && (
        <Tooltip
          content={
            isComplete
              ? intl.formatMessage({
                  id: 'templates.card-done-state.mark-incomplete',
                  defaultMessage: 'Mark incomplete',
                  description: 'Tooltip text for marking a card as incomplete',
                })
              : intl.formatMessage({
                  id: 'templates.card-done-state.mark-complete',
                  defaultMessage: 'Mark complete',
                  description: 'Tooltip text for marking a card as complete',
                })
          }
          position="top"
          tag="span"
        >
          <div className={styles.animationContainer}>
            <Button
              className={cx(buttonClassName, {
                [styles.checkmarkButton]: true,
                [cardFrontStyles.buttonAnimation]:
                  children && !isComplete && animateFromExternal,
                [styles.buttonAnimation]:
                  children && !isComplete && !animateFromExternal,
              })}
              onClick={updateCardDoneStatus}
              appearance="subtle"
              data-testid={getTestId<CardTestIds>(
                'card-done-state-completion-button',
              )}
              onKeyDown={handleKeyDown}
              {...(isAriaHidden ? { 'aria-hidden': true, tabIndex: -1 } : {})}
            >
              <div
                className={cx({
                  [styles.iconContainer]: true,
                  [styles.buttonRotatingAnimation]: isButtonAnimating,
                  [styles.iconContainerSmall]: isSmall,
                })}
                data-testid="card-done-state-icon-container"
              >
                {isComplete ? (
                  <SuccessIcon
                    label={intl.formatMessage({
                      id: 'templates.card-done-state.complete',
                      defaultMessage: 'Complete',
                      description: 'Label for marking a card as complete',
                    })}
                    color={
                      successIconColor ?? token('color.icon.success', '#22A06B')
                    }
                  />
                ) : (
                  <RadioUncheckedIcon
                    color={
                      uncheckedIconColor ??
                      token('color.icon.subtle', '#626F86')
                    }
                    label={intl.formatMessage({
                      id: 'templates.card-done-state.incomplete',
                      defaultMessage: 'Incomplete',
                      description: 'Label for marking a card as incomplete',
                    })}
                  />
                )}
              </div>
              {isButtonAnimating && (
                <div
                  className={cx(styles.linesContainer, {
                    [styles.linesContainerSmall]: isSmall,
                  })}
                  data-testid="card-done-state-lines"
                >
                  {Array.from({ length: 8 }, (_, index) => index).map(
                    (index) => (
                      <div
                        key={index}
                        data-testid={`card-done-state-line-${index}`}
                        className={cx({
                          [styles.line]: true,
                          [styles.lineAnimation]: true,
                          [styles.colorfulLine]: isLineColorful,
                          [styles.lineAnimationSmall]: isSmall,
                        })}
                        style={{
                          backgroundColor: getLineColor(index),
                        }}
                        onAnimationEnd={resetAnimation}
                      />
                    ),
                  )}
                </div>
              )}
            </Button>
          </div>
        </Tooltip>
      )}

      {children && (
        <span
          className={cx(childrenClassName, {
            [styles.cardTitle]: true,
            [cardFrontStyles.cardTitleAnimation]:
              !isComplete && showDoneStatebutton,
            [styles.cardTitleAnimation]:
              !isComplete && !animateFromExternal && showDoneStatebutton,
            [styles.isComplete]: isComplete && !manualCompleteStyle,
          })}
        >
          {children}
        </span>
      )}
    </div>
  );
};
